import type { ObservabilityEvent } from '../observability.events';
import type { ObservabilityReporter } from '../observability';
import {
	observabilityDb,
	ObservabilityEventRow,
} from '../storage/observability-db';
import { internDocument } from '../storage/intern-document';
import { sweepExpiredEvents, trimToByteBudget } from '../storage/eviction';
import { onAppHidden } from '@shared/lib/app-lifecycle';

const FLUSH_INTERVAL_MS = 30_000;

interface PendingEntry {
	/** The first occurrence -- timestamp and payload are its, not the latest
	 * repeat's. Only `occurrences` advances on a repeat. */
	event: ObservabilityEvent;
	occurrences: number;
}

type FallbackEvent = ObservabilityEvent & { occurrences: number };

// In-memory buffer, flushed to Dexie periodically. A render loop firing an
// error thousands of times a second must not become thousands of IndexedDB
// write transactions -- see the topic file's "Write path" section.
const pending = new Map<string, PendingEntry>();

// If Dexie itself is unusable (private browsing, exhausted quota, a
// corrupted store), events land here instead of being lost. This exists
// specifically so `db_open_failure` -- the event explaining a permanent
// blank screen -- can still be recorded when the thing it's reporting on is
// the very store that would otherwise hold it. Anything reading the store
// (the future zip export, a devtools panel) must read both tiers.
const memoryFallback: Array<FallbackEvent> = [];
let dbUsable = true;
let lifecycleHooksInstalled = false;

// Same shape as debug-log/ring-buffer.ts's pub-sub -- lets a live view (the
// devtools panel's Events tab) know a flush just happened instead of
// requiring a manual refresh. Deliberately not a `report()`-time notify:
// that fires far too often (every call, pre-dedupe) for what's meant to be
// "something new landed in the store", which is only true once a flush
// actually runs.
const listeners = new Set<() => void>();

function notify() {
	for (const listener of listeners) {
		listener();
	}
}

/** Fires after every flush attempt -- Dexie success or memory fallback --
 * since either one means there's new data worth re-reading. */
export function subscribeToEventFlush(listener: () => void): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

function categoryFor(event: ObservabilityEvent): string | undefined {
	switch (event.name) {
		case 'client_error':
			return event.payload.category ?? event.payload.source;
		case 'query_error':
			return event.payload.operation;
		case 'sw_download_failure':
			return event.payload.cacheName;
		default:
			return undefined;
	}
}

/** `name + category`, deliberately excluding timestamp/variables/stack --
 * including those would mean nothing ever coalesces. */
function dedupeKey(event: ObservabilityEvent): string {
	return `${event.name}:${categoryFor(event) ?? ''}`;
}

function installLifecycleHooks() {
	if (lifecycleHooksInstalled) {
		return;
	}
	lifecycleHooksInstalled = true;

	setInterval(() => void flush(), FLUSH_INTERVAL_MS);

	onAppHidden(() => void flush());

	void sweepExpiredEvents();
}

async function resolvePayload(
	event: ObservabilityEvent,
): Promise<Record<string, unknown>> {
	if (event.name === 'query_error') {
		const { document: documentText, ...rest } = event.payload;
		const documentHash = await internDocument(documentText);
		return { ...rest, documentHash };
	}
	if ('payload' in event) {
		return { ...event.payload };
	}
	return {};
}

async function buildRow(entry: PendingEntry): Promise<ObservabilityEventRow> {
	const { event, occurrences } = entry;
	const payload = await resolvePayload(event);
	const row = {
		name: event.name,
		appKey: event.appKey,
		template: event.template,
		version: event.version,
		buildNumber: event.buildNumber,
		swVersion: event.swVersion,
		timestamp: event.timestamp,
		dedupeKey: dedupeKey(event),
		occurrences,
		payload,
	};
	return { ...row, approxBytes: JSON.stringify(row).length };
}

function toFallbackEvents(entries: Array<PendingEntry>): Array<FallbackEvent> {
	return entries.map(({ event, occurrences }) => ({ ...event, occurrences }));
}

async function flush(): Promise<void> {
	if (pending.size === 0) {
		return;
	}
	const entries = Array.from(pending.values());
	pending.clear();

	if (!dbUsable) {
		memoryFallback.push(...toFallbackEvents(entries));
		notify();
		return;
	}

	try {
		const rows = await Promise.all(entries.map(buildRow));
		await observabilityDb.events.bulkAdd(rows);
		await sweepExpiredEvents();
		await trimToByteBudget();
		notify();
		// Dev-only breadcrumb -- console-reporter (bootstrap.ts) only proves
		// report() was called; nothing else confirms the flush to Dexie
		// actually landed, which is exactly the gap that made this failure
		// mode hard to tell apart from "nothing happened yet" from the
		// console-reporter log alone.
		if (import.meta.env.DEV) {
			console.debug(
				`[observability] flushed ${rows.length} event(s) to cf-observability`,
			);
		}
	} catch (error) {
		dbUsable = false;
		memoryFallback.push(...toFallbackEvents(entries));
		notify();
		if (import.meta.env.DEV) {
			console.warn(
				'[observability] flush to cf-observability failed -- falling back to memory',
				error,
			);
		}
	}
}

export const localReporter: ObservabilityReporter = {
	name: 'local',
	report(event) {
		installLifecycleHooks();
		const key = dedupeKey(event);
		const existing = pending.get(key);
		if (existing) {
			existing.occurrences += 1;
		} else {
			pending.set(key, { event, occurrences: 1 });
		}
	},
};

/** Whatever couldn't reach Dexie. The future zip export must merge this
 * with the Dexie tier -- reading only the happy-path store misses exactly
 * the failures most worth capturing. */
export function getMemoryFallbackEvents(): ReadonlyArray<FallbackEvent> {
	return memoryFallback;
}

/**
 * Drops the un-flushed buffer and the memory-fallback tier. Paired with a
 * `observabilityDb.events.clear()` by the devtools panel's "Clear" -- the
 * point of that button is "reset to nothing so I can reproduce cleanly",
 * and clearing only the Dexie half would leave fallback rows reappearing
 * in the panel with no way to get rid of them. `pending` goes too, or the
 * next flush would immediately repopulate what was just cleared.
 *
 * Deliberately does NOT reset `dbUsable`: if Dexie was found unusable,
 * that's a property of the device, not of the data, and optimistically
 * retrying on every clear would send each subsequent flush back through
 * the failing path.
 */
export function clearBufferedEvents(): void {
	pending.clear();
	memoryFallback.length = 0;
	notify();
}
